import { useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { Guide } from './types';
import { slideFor, type SlidesManifest } from './slides';
import { fmtDateTimeShort } from '../shared/listCells';

/**
 * Duży podgląd slajdów przewodnika (21.10.2026): slajd z opisem kroku,
 * przewijanie strzałkami (przyciski i klawiatura ← →), Esc zamyka.
 * „Pokaż w aplikacji” przenosi panel na ten krok, zamyka podgląd i prowadzi
 * do elementu na ekranie.
 */
export const SlideViewer = ({
  guide,
  step,
  manifest,
  onStep,
  onClose,
  onShowInApp,
}: {
  guide: Guide;
  step: number;
  manifest: SlidesManifest | null;
  onStep: (step: number) => void;
  onClose: () => void;
  onShowInApp: (step: number) => void;
}) => {
  const total = guide.steps.length;
  const current = guide.steps[step];
  const slide = slideFor(manifest, guide.flagKey, step);
  const prev = () => {
    if (step > 0) onStep(step - 1);
  };
  const next = () => {
    if (step < total - 1) onStep(step + 1);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!current) return null;
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      aria-labelledby="guide-viewer-title"
      data-guide-overlay=""
      data-testid="guide-viewer"
    >
      <DialogTitle id="guide-viewer-title">
        {guide.title} — slajd {step + 1} z {total}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          {slide ? (
            <Box
              component="img"
              src={slide.url}
              alt={`Slajd ${step + 1}: ${current.title}`}
              data-testid="guide-viewer-image"
              sx={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: 'calc(100vh - 300px)',
                mx: 'auto',
                objectFit: 'contain',
                border: 1,
                borderColor: 'divider',
              }}
            />
          ) : (
            <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'action.hover', borderRadius: 1 }} data-testid="guide-viewer-no-slide">
              <Typography variant="body2" color="text.secondary">
                Dla tego kroku nie ma jeszcze slajdu — pokażemy go na żywo w aplikacji.
              </Typography>
            </Box>
          )}
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Krok {step + 1}: {current.title}
          </Typography>
          <Typography variant="body2">{current.description}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Co zrobić: {current.action}
          </Typography>
          {manifest?.recordedAt ? (
            <Typography variant="caption" color="text.secondary">
              Slajdy z danych pokazowych, nagrane {fmtDateTimeShort(manifest.recordedAt)}
              {manifest.appVersion ? ` (wersja ${manifest.appVersion})` : ''}.
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1}>
          <Button onClick={prev} disabled={step === 0} startIcon={<ChevronLeftIcon />} data-testid="guide-viewer-prev">
            Wstecz
          </Button>
          <Button onClick={next} disabled={step >= total - 1} endIcon={<ChevronRightIcon />} data-testid="guide-viewer-next">
            Dalej
          </Button>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button onClick={() => onShowInApp(step)} data-testid="guide-viewer-show">
            Pokaż w aplikacji
          </Button>
          <Button onClick={onClose} variant="contained">
            Zamknij
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
